import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ProgressRing from "@/components/ProgressRing";
import LicenseUpsell from "@/components/LicenseUpsell";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Clock, Target, Trophy, PlayCircle } from "lucide-react";

export default function ModuleView() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const modQ = useQuery({ queryKey: ["module", moduleId], queryFn: async () => (await api.get(`/course/${moduleId}`)).data, retry: false });
  const progQ = useQuery({ queryKey: ["progress"], queryFn: async () => (await api.get("/progress")).data });

  const m = modQ.data;
  const mp = progQ.data?.modules?.[moduleId];
  const done = new Set(mp?.completed_lesson_ids || []);

  if (modQ.error?.response?.status === 403) {
    return (
      <div className="min-h-screen bg-background grain">
        <Navbar />
        <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> Zurück zum Kurs
          </Link>
          <LicenseUpsell />
        </main>
      </div>
    );
  }

  if (modQ.isLoading || !m) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-5 py-10">
          <div className="h-60 rounded-3xl glass animate-pulse" />
        </div>
      </div>
    );
  }

  const quiz = mp?.quiz;

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Zurück zum Kurs
        </Link>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative rounded-3xl overflow-hidden mb-8">
          <img src={m.cover} alt={m.title} className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-card/40" />
          <div className="relative p-7 sm:p-10">
            <div className="flex items-start justify-between gap-6">
              <div>
                <span className="font-mono text-sm text-gold">Modul {String(m.number).padStart(2, "0")}</span>
                <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2">{m.title}</h1>
                <p className="text-foreground/70 mt-2 text-lg">{m.subtitle}</p>
              </div>
              <ProgressRing percent={mp?.percent ?? 0} size={84} stroke={7} />
            </div>
            <div className="mt-6 rounded-xl glass p-4 flex gap-3">
              <Target className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Lernziel</div>
                <p className="text-sm text-foreground/85 mt-1 leading-relaxed">{m.goal}</p>
              </div>
            </div>
            <div className="flex items-center gap-5 mt-5 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {m.duration_min} Min</span>
              <span className="flex items-center gap-1.5"><PlayCircle className="h-4 w-4" /> {m.lessons.length} Lektionen</span>
              <span className="flex items-center gap-1.5"><Trophy className="h-4 w-4" /> {m.quiz.length} Quizfragen</span>
            </div>
          </div>
        </motion.div>

        {/* Lessons */}
        <h2 className="font-heading font-bold text-xl mb-4">Lektionen</h2>
        <div className="space-y-3">
          {m.lessons.map((l, i) => {
            const isDone = done.has(l.id);
            return (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={`/module/${moduleId}/lesson/${l.id}`}
                  data-testid={`lesson-item-${l.id}`}
                  className="group flex items-center gap-4 rounded-xl glass p-4 hover:glow-gold transition-all duration-300"
                >
                  <div className="flex-shrink-0">
                    {isDone ? (
                      <CheckCircle2 className="h-6 w-6 text-gold" />
                    ) : (
                      <Circle className="h-6 w-6 text-muted-foreground/40 group-hover:text-gold transition-colors" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium group-hover:text-gold transition-colors">{l.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" /> {l.minutes} Min</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-gold group-hover:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Quiz card */}
        <div className="mt-8 rounded-2xl glass-strong p-6 sm:p-7">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center">
                <Trophy className="h-6 w-6 text-gold" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">Modul-Quiz</h3>
                <p className="text-sm text-muted-foreground">
                  {quiz ? (
                    quiz.passed ? (
                      <span className="text-gold">Bestanden – {quiz.score}/{quiz.total} richtig ({quiz.percent}%)</span>
                    ) : (
                      <span>Letzter Versuch: {quiz.score}/{quiz.total} ({quiz.percent}%) – nicht bestanden</span>
                    )
                  ) : (
                    `${m.quiz.length} Fragen · 70% zum Bestehen`
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/module/${moduleId}/quiz`)}
              data-testid="start-quiz-button"
              className="px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300"
            >
              {quiz ? "Quiz wiederholen" : "Quiz starten"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
